import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  TrashIcon,
  KeyIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  EyeSlashIcon,
  ComputerDesktopIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import { api, downloadAuthenticated } from '../../services/api';

// ────────────────────────────────────────────────────────────────────
// Desktop worker pairing — download bundle + install commands
// ────────────────────────────────────────────────────────────────────

export function buildInstallCommands({ apiUrl, token, agentName }) {
  const pairingToken = token || 'wga_PASTE_PAIRING_TOKEN_FROM_DASHBOARD';
  const folderHint = (agentName || 'agent').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '') || 'agent';
  return `# Pair "${agentName || 'VA Agent'}" with this Mac
# Prerequisites: Node.js 18+ (node -v) and GoLogin desktop installed.

# 1. Unzip the downloaded bundle, then open Terminal in the folder:
cd ~/Downloads/wingr-desktop-agent-${folderHint}/desktop-agent

# 2. Install dependencies (first time only on this Mac)
npm install

# 3. .env is pre-filled in the download — verify:
cat .env

# You should see:
# WINGR_API_URL=${apiUrl}
# WINGR_AGENT_TOKEN=${pairingToken}

# 4. Start the worker (keep this terminal open while jobs run)
npm start

# The agent should show "Online" in the dashboard within ~10 seconds.
# GoLogin API token, follow timings, and concurrency are set in the
# dashboard Setup tab — not in this .env file.

# ── Another desktop / another VA Agent ──
# • New VA Agent → new token automatically (existing agents unaffected).
# • Same agent, another Mac → click "Add desktop" in Setup for an extra token.
# Each desktop uses its own WINGR_AGENT_TOKEN in its own .env file.

# ── Windows VPS ──
# Double-click Wingr-agent on the desktop (or Wingr-agent.cmd in the folder).
# The first start places that shortcut, with the wing icon, on the desktop
# and in Startup, so it opens again after a reboot.
# Start, stop, and update can also be sent from this dashboard.`;
}

function defaultCopy(text) {
  if (!text) return;
  navigator.clipboard?.writeText(text).then(
    () => toast.success('Copied'),
    () => toast.error('Copy failed')
  );
}

export function DesktopSetupPanel({
  agent,
  /** Pre-selected pairing for download/commands (post-create wizard). */
  initialPairingId = null,
  /** Token shown immediately after create/add (one-time reveal). */
  initialToken = null,
  onCopy,
  compact = false
}) {
  const [pairings, setPairings] = useState(agent?.desktopPairings || []);
  const [selectedPairingId, setSelectedPairingId] = useState(
    initialPairingId || agent?.desktopPairings?.[0]?._id || null
  );
  const [revealedTokens, setRevealedTokens] = useState(
    initialToken && (initialPairingId || agent?.desktopPairings?.[0]?._id)
      ? { [String(initialPairingId || agent.desktopPairings[0]._id)]: initialToken }
      : {}
  );
  const [tokenLoading, setTokenLoading] = useState(false);
  const [addingDesktop, setAddingDesktop] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showCommands, setShowCommands] = useState(!compact);
  const [debugLogsBusy, setDebugLogsBusy] = useState(false);
  const [debugLogsOpen, setDebugLogsOpen] = useState(false);
  const [debugLogLines, setDebugLogLines] = useState([]);
  const [debugLogsMeta, setDebugLogsMeta] = useState(
    agent?.debugConsoleLogs || { enabled: false, expiresAt: null }
  );
  const resolvedApiUrl = typeof window !== 'undefined' ? window.location.origin : 'https://wingr.io';

  const fetchPairings = useCallback(async () => {
    if (!agent?._id) return;
    try {
      const res = await api.get(`/admin/va/ai-agents/${agent._id}/pairings`);
      if (res.data.success) {
        setPairings(res.data.pairings || []);
        setSelectedPairingId((prev) => {
          if (prev && res.data.pairings.some((p) => String(p._id) === String(prev))) return prev;
          return res.data.pairings[0]?._id || null;
        });
      }
    } catch (_) { /* ignore */ }
  }, [agent?._id]);

  useEffect(() => {
    if (agent?.debugConsoleLogs) {
      setDebugLogsMeta(agent.debugConsoleLogs);
    }
  }, [agent?.debugConsoleLogs?.enabled, agent?.debugConsoleLogs?.expiresAt]);

  useEffect(() => {
    if (agent?.desktopPairings?.length) {
      setPairings(agent.desktopPairings);
      if (!selectedPairingId) {
        setSelectedPairingId(initialPairingId || agent.desktopPairings[0]?._id || null);
      }
    } else {
      fetchPairings();
    }
  }, [agent?._id, agent?.desktopPairings, fetchPairings, initialPairingId, selectedPairingId]);

  const selectedPairing = pairings.find((p) => String(p._id) === String(selectedPairingId)) || pairings[0];
  const selectedToken = selectedPairingId ? revealedTokens[String(selectedPairingId)] : null;
  const tokenVisible = Boolean(selectedToken);

  const revealSelectedToken = async () => {
    if (!agent?._id || !selectedPairingId) return;
    if (tokenVisible) {
      setRevealedTokens((prev) => {
        const next = { ...prev };
        delete next[String(selectedPairingId)];
        return next;
      });
      return;
    }
    if (revealedTokens[String(selectedPairingId)]) {
      return;
    }
    setTokenLoading(true);
    try {
      const res = await api.get(`/admin/va/ai-agents/${agent._id}/token`, {
        params: { pairingId: selectedPairingId }
      });
      if (res.data.success) {
        setRevealedTokens((prev) => ({
          ...prev,
          [String(selectedPairingId)]: res.data.desktopAgentToken
        }));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to read token');
    } finally {
      setTokenLoading(false);
    }
  };

  const addDesktop = async () => {
    if (!agent?._id) return;
    setAddingDesktop(true);
    try {
      const res = await api.post(`/admin/va/ai-agents/${agent._id}/pairings`, {
        label: `Desktop ${pairings.length + 1}`
      });
      if (res.data.success) {
        const created = res.data.pairing;
        setPairings(res.data.pairings || []);
        setSelectedPairingId(created._id);
        setRevealedTokens((prev) => ({ ...prev, [String(created._id)]: created.token }));
        toast.success('New desktop token created — existing desktops keep working');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add desktop');
    } finally {
      setAddingDesktop(false);
    }
  };

  const removeDesktop = async (pairing) => {
    if (!agent?._id || !pairing?._id) return;
    const label = pairing.label || 'this desktop';
    if (!window.confirm(
      `Remove ${label}?\n\nThe token will stop working immediately and that desktop worker will start getting 401s on its next heartbeat. Other desktops keep working.`
    )) return;
    try {
      const res = await api.delete(`/admin/va/ai-agents/${agent._id}/pairings/${pairing._id}`);
      if (res.data.success) {
        const next = res.data.pairings || [];
        setPairings(next);
        setRevealedTokens((prev) => {
          const out = { ...prev };
          delete out[String(pairing._id)];
          return out;
        });
        if (String(selectedPairingId) === String(pairing._id)) {
          setSelectedPairingId(next[0]?._id || null);
        }
        toast.success(`${label} removed`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove desktop');
    }
  };

  const installScript = useMemo(
    () => buildInstallCommands({
      apiUrl: resolvedApiUrl,
      token: tokenVisible ? selectedToken : null,
      agentName: agent?.name
    }),
    [resolvedApiUrl, selectedToken, tokenVisible, agent?.name]
  );

  const handleDownload = async () => {
    if (!agent?._id || !selectedPairingId) return;
    setDownloading(true);
    try {
      const qs = new URLSearchParams({
        apiUrl: resolvedApiUrl,
        pairingId: String(selectedPairingId)
      });
      await downloadAuthenticated(
        `/admin/va/ai-agents/${agent._id}/desktop-agent/download?${qs}`,
        `wingr-desktop-agent.zip`
      );
      toast.success('Downloaded — unzip on the target Mac, then npm install && npm start');
    } catch (err) {
      toast.error(err.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const copy = onCopy || defaultCopy;
  const cardPad = compact ? 'p-3' : 'p-4';
  const helpText = compact
    ? 'Each Mac needs its own pairing token. Add desktop for another machine.'
    : null;

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      <div className={`rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20 ${cardPad} space-y-3`}>
        <div className="flex items-start gap-3">
          <ComputerDesktopIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-300 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
            <p className="font-medium text-gray-900 dark:text-white">Each desktop gets its own pairing token</p>
            {helpText ? (
              <p>{helpText}</p>
            ) : (
              <p>
                Creating a <strong>new VA Agent</strong> mints a fresh <code className="font-mono text-[10px]">wga_…</code> token —
                existing agents and their desktops are untouched. To add another Mac to the <em>same</em> agent,
                click <strong>Add desktop</strong> below for an extra token (old ones keep working).
              </p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Paired desktops</span>
            <button
              type="button"
              onClick={addDesktop}
              disabled={addingDesktop || !agent?._id}
              className="btn-secondary !py-1 !px-2.5 text-xs inline-flex items-center gap-1"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              {addingDesktop ? 'Creating…' : 'Add desktop'}
            </button>
          </div>
          {pairings.length ? (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 max-h-48 overflow-y-auto bg-white dark:bg-gray-900/40">
              {pairings.map((p) => {
                const isSelected = String(selectedPairingId) === String(p._id);
                const isLast = pairings.length <= 1;
                return (
                  <div
                    key={p._id}
                    className={`flex items-stretch ${
                      isSelected ? 'bg-indigo-50 dark:bg-indigo-950/40' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedPairingId(p._id)}
                      className="flex-1 text-left px-3 py-2 text-xs flex items-center justify-between gap-2 min-w-0"
                    >
                      <span className="font-medium text-gray-900 dark:text-white truncate">{p.label}</span>
                      <span className="flex items-center gap-2 text-gray-500 flex-shrink-0">
                        <code className="font-mono text-[10px]">{p.tokenSuffix}</code>
                        {p.online ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            online
                          </span>
                        ) : (
                          <span>offline</span>
                        )}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeDesktop(p); }}
                      disabled={isLast}
                      className="px-3 text-rose-500 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300 disabled:text-gray-300 dark:disabled:text-gray-600 disabled:cursor-not-allowed flex items-center"
                      title={isLast ? 'Cannot remove the only paired desktop' : `Remove ${p.label}`}
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-gray-500">No desktops paired yet.</p>
          )}
        </div>

        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading || !agent?._id || !selectedPairingId}
          className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          {downloading ? 'Preparing zip…' : 'Download desktop agent (.zip)'}
        </button>
        {selectedPairing?.host && (
          <p className="text-[11px] text-gray-500">
            Selected: {selectedPairing.label}
            {selectedPairing.lastSeenAt && ` · last seen ${new Date(selectedPairing.lastSeenAt).toLocaleString()}`}
            {selectedPairing.host && ` · ${selectedPairing.host}`}
          </p>
        )}

        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">VPS console logs (opt-in)</p>
              <p className="text-[11px] text-gray-500">
                Off by default. Ships small batches for 15 minutes when enabled (agent ≥ 0.3.40).
              </p>
            </div>
            <span className={`text-[11px] font-medium ${debugLogsMeta?.enabled ? 'text-emerald-600' : 'text-gray-400'}`}>
              {debugLogsMeta?.enabled
                ? `ON until ${debugLogsMeta.expiresAt ? new Date(debugLogsMeta.expiresAt).toLocaleTimeString() : '?'}`
                : 'OFF'}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={debugLogsBusy || !agent?._id || debugLogsMeta?.enabled}
              onClick={async () => {
                setDebugLogsBusy(true);
                try {
                  const res = await api.post(`/admin/va/ai-agents/${agent._id}/debug-console-logs/enable`, { minutes: 15 });
                  if (res.data.success) {
                    setDebugLogsMeta({ enabled: true, expiresAt: res.data.expiresAt });
                    toast.success('Console shipping on for 15 minutes');
                  }
                } catch (err) {
                  toast.error(err.response?.data?.error || 'Failed to enable');
                } finally {
                  setDebugLogsBusy(false);
                }
              }}
              className="btn-secondary text-xs !py-1"
            >
              Enable 15m
            </button>
            <button
              type="button"
              disabled={debugLogsBusy || !agent?._id || !debugLogsMeta?.enabled}
              onClick={async () => {
                setDebugLogsBusy(true);
                try {
                  await api.post(`/admin/va/ai-agents/${agent._id}/debug-console-logs/disable`);
                  setDebugLogsMeta({ enabled: false, expiresAt: null });
                  toast.success('Console shipping off');
                } catch (err) {
                  toast.error(err.response?.data?.error || 'Failed to disable');
                } finally {
                  setDebugLogsBusy(false);
                }
              }}
              className="btn-secondary text-xs !py-1"
            >
              Disable
            </button>
            <button
              type="button"
              disabled={debugLogsBusy || !agent?._id}
              onClick={async () => {
                setDebugLogsBusy(true);
                try {
                  const res = await api.get(`/admin/va/ai-agents/${agent._id}/debug-console-logs?limit=200`);
                  if (res.data.success) {
                    setDebugLogLines(res.data.logs || []);
                    setDebugLogsMeta({ enabled: !!res.data.enabled, expiresAt: res.data.expiresAt || null });
                    setDebugLogsOpen(true);
                  }
                } catch (err) {
                  toast.error(err.response?.data?.error || 'Failed to load logs');
                } finally {
                  setDebugLogsBusy(false);
                }
              }}
              className="btn-secondary text-xs !py-1"
            >
              View logs
            </button>
          </div>
          {debugLogsOpen && (
            <pre className="max-h-56 overflow-auto rounded bg-gray-950 text-gray-100 text-[10px] leading-snug p-2 whitespace-pre-wrap">
              {(debugLogLines.length
                ? debugLogLines.map((e) => `${e.ts || ''} ${e.line || ''}`).join('\n')
                : '(no lines yet — enable shipping, wait ~20s for a batch)')}
            </pre>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <KeyIcon className="w-4 h-4" /> Pairing token
          {selectedPairing?.label && (
            <span className="text-xs font-normal text-gray-500">({selectedPairing.label})</span>
          )}
        </h4>
        <div className="flex gap-2">
          <input
            readOnly
            value={tokenVisible && selectedToken ? selectedToken : '••••••••••••••••••••••••••••••••'}
            className="form-input flex-1 font-mono text-xs"
          />
          <button
            type="button"
            onClick={revealSelectedToken}
            disabled={tokenLoading || !selectedPairingId}
            className="btn-secondary !px-3"
            title={tokenVisible ? 'Hide' : 'Reveal'}
          >
            {tokenLoading ? '…' : tokenVisible ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={() => copy(selectedToken)}
            disabled={!tokenVisible || !selectedToken}
            className="btn-secondary !px-3 disabled:opacity-40"
            title="Copy token"
          >
            <ClipboardDocumentIcon className="w-4 h-4" />
          </button>
        </div>
        {!compact && (
          <p className="text-[11px] text-gray-500">
            Included in the downloaded zip for the selected desktop. Adding a new desktop never invalidates older tokens.
          </p>
        )}
      </div>

      <div>
        <button
          type="button"
          onClick={() => setShowCommands((v) => !v)}
          className="text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-300"
        >
          {showCommands ? 'Hide' : 'Show'} install commands
        </button>
        {showCommands && (
          <div className="mt-2 relative">
            <pre className="text-[11px] bg-gray-900 text-gray-100 rounded-lg p-3 overflow-x-auto leading-relaxed max-h-64 overflow-y-auto">
              {installScript}
            </pre>
            <button
              type="button"
              onClick={() => copy(installScript)}
              className="absolute top-2 right-2 btn-secondary !py-1 !px-2 text-[10px] inline-flex items-center gap-1"
            >
              <ClipboardDocumentIcon className="w-3 h-3" /> Copy all
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function GoLoginSetupSection({ agent, onAgentUpdated, compact = false }) {
  const [gologinTokenInput, setGologinTokenInput] = useState('');
  const [gologinTokenVisible, setGologinTokenVisible] = useState(false);
  const [gologinTokenSaving, setGologinTokenSaving] = useState(false);
  const [gologinTokenLoaded, setGologinTokenLoaded] = useState(false);
  const [gologinFolderInput, setGologinFolderInput] = useState(agent?.gologinFolder?.name || '');
  const [gologinFolderMode, setGologinFolderMode] = useState(
    agent?.gologinFolder?.mode === 'create_if_missing' ? 'new' : 'existing'
  );
  const [gologinFolders, setGologinFolders] = useState([]);
  const [gologinFoldersLoading, setGologinFoldersLoading] = useState(false);
  const [gologinFolderSaving, setGologinFolderSaving] = useState(false);
  const radioName = `agentGologinFolderMode-${agent?._id || 'new'}`;

  useEffect(() => {
    setGologinTokenInput('');
    setGologinTokenVisible(false);
    setGologinTokenLoaded(false);
    setGologinFolderInput(agent?.gologinFolder?.name || '');
    setGologinFolderMode(
      agent?.gologinFolder?.mode === 'create_if_missing' ? 'new' : 'existing'
    );
  }, [agent?._id]);

  const fetchGologinFolders = useCallback(async () => {
    setGologinFoldersLoading(true);
    try {
      const res = await api.get('/admin/va/gologin/folders');
      const folders = Array.isArray(res.data?.folders) ? res.data.folders : [];
      setGologinFolders(folders);
      return folders;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load GoLogin folders');
      setGologinFolders([]);
      return [];
    } finally {
      setGologinFoldersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!agent?._id) return;
    fetchGologinFolders();
  }, [agent?._id, fetchGologinFolders]);

  const gologinFolderOptions = useMemo(() => {
    const current = gologinFolderInput.trim();
    if (!current) return gologinFolders;
    const inList = gologinFolders.some((f) => f.name === current);
    if (inList) return gologinFolders;
    return [{ id: '__saved', name: current }, ...gologinFolders];
  }, [gologinFolders, gologinFolderInput]);

  const revealGologinToken = async () => {
    try {
      const res = await api.get(`/admin/va/ai-agents/${agent._id}/secrets`);
      if (res.data.success) {
        setGologinTokenInput(res.data.gologinApiToken || '');
        setGologinTokenVisible(true);
        setGologinTokenLoaded(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to read GoLogin token');
    }
  };

  const saveGologinToken = async () => {
    setGologinTokenSaving(true);
    try {
      const res = await api.patch(`/admin/va/ai-agents/${agent._id}`, {
        gologinApiToken: gologinTokenInput.trim()
      });
      toast.success(gologinTokenInput.trim() ? 'GoLogin token saved' : 'GoLogin token cleared');
      setGologinTokenVisible(false);
      onAgentUpdated?.(res.data?.agent || { hasGologinApiToken: Boolean(gologinTokenInput.trim()) });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setGologinTokenSaving(false);
    }
  };

  const saveGologinFolder = async () => {
    setGologinFolderSaving(true);
    try {
      const trimmed = gologinFolderInput.trim();
      if (!trimmed) {
        toast.error('Select or enter a folder name');
        return;
      }
      const mode = gologinFolderMode === 'new' ? 'create_if_missing' : 'existing';
      const res = await api.patch(`/admin/va/ai-agents/${agent._id}`, {
        gologinFolder: { name: trimmed, mode }
      });
      toast.success(`Auto-import set to "${trimmed}"`);
      onAgentUpdated?.(res.data?.agent || { gologinFolder: { name: trimmed, mode, autoImport: true } });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save GoLogin folder');
    } finally {
      setGologinFolderSaving(false);
    }
  };

  const disableGologinFolder = async () => {
    setGologinFolderSaving(true);
    try {
      const res = await api.patch(`/admin/va/ai-agents/${agent._id}`, { gologinFolder: null });
      setGologinFolderInput('');
      toast.success('GoLogin auto-import disabled');
      onAgentUpdated?.(res.data?.agent || { gologinFolder: null });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to disable GoLogin auto-import');
    } finally {
      setGologinFolderSaving(false);
    }
  };

  const sectionClass = compact
    ? 'rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/60 p-4 space-y-3'
    : 'space-y-2';

  return (
    <>
      <div className={sectionClass}>
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <KeyIcon className="w-4 h-4" /> GoLogin API token
            {agent.hasGologinApiToken ? (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                Set
              </span>
            ) : String(agent?.config?.browserEngine || '').toLowerCase() === 'orbex' ? (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                Not required
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                Missing
              </span>
            )}
          </h4>
        </div>
        <div className="flex gap-2">
          <input
            type={gologinTokenVisible ? 'text' : 'password'}
            autoComplete="off"
            value={
              gologinTokenVisible
                ? gologinTokenInput
                : (agent.hasGologinApiToken ? '••••••••••••••••••••••••••••••••' : '')
            }
            onChange={(e) => setGologinTokenInput(e.target.value)}
            placeholder={agent.hasGologinApiToken ? '' : 'Paste your GoLogin dev token'}
            className="form-input flex-1 font-mono text-xs"
            readOnly={!gologinTokenVisible && agent.hasGologinApiToken}
          />
          <button
            type="button"
            onClick={() => {
              if (gologinTokenVisible) {
                setGologinTokenVisible(false);
                return;
              }
              if (agent.hasGologinApiToken && !gologinTokenLoaded) {
                revealGologinToken();
              } else {
                setGologinTokenVisible(true);
              }
            }}
            className="btn-secondary !px-3"
            title={gologinTokenVisible ? 'Hide' : 'Reveal / edit'}
          >
            {gologinTokenVisible ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={saveGologinToken}
            disabled={!gologinTokenVisible || gologinTokenSaving}
            className="btn-primary !px-3 disabled:opacity-40"
            title="Save"
          >
            {gologinTokenSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
        {!compact && (
          <p className="text-[11px] text-gray-500">
            Personal Area → API Token in GoLogin. Stored on the server and sent to the
            desktop per job — never written to disk on the Mac.
          </p>
        )}
      </div>

      <div className={sectionClass}>
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <KeyIcon className="w-4 h-4" /> GoLogin auto-import folder
            {agent.gologinFolder?.autoImport ? (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                {agent.gologinFolder.name}
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                Off
              </span>
            )}
          </h4>
        </div>
        <div className={`space-y-3 ${compact ? '' : 'rounded-lg border border-gray-200 dark:border-gray-700 p-3'}`}>
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Folder</p>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="radio"
                name={radioName}
                checked={gologinFolderMode === 'existing'}
                onChange={() => setGologinFolderMode('existing')}
                disabled={gologinFolderSaving}
              />
              Use existing folder
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="radio"
                name={radioName}
                checked={gologinFolderMode === 'new'}
                onChange={() => setGologinFolderMode('new')}
                disabled={gologinFolderSaving}
              />
              Create new folder
            </label>
          </div>

          {gologinFolderMode === 'existing' ? (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select folder
              </label>
              {gologinFoldersLoading ? (
                <p className="text-xs text-gray-500">Loading folders…</p>
              ) : (
                <select
                  value={gologinFolderInput}
                  onChange={(e) => setGologinFolderInput(e.target.value)}
                  className="form-select w-full text-xs"
                  disabled={gologinFolderSaving || gologinFolderOptions.length === 0}
                >
                  {gologinFolderOptions.length === 0 ? (
                    <option value="">No folders found — create a new one</option>
                  ) : (
                    gologinFolderOptions.map((folder) => (
                      <option key={folder.id || folder.name} value={folder.name}>
                        {folder.name}
                      </option>
                    ))
                  )}
                </select>
              )}
              <button
                type="button"
                onClick={fetchGologinFolders}
                disabled={gologinFoldersLoading || gologinFolderSaving}
                className="mt-2 text-xs text-indigo-600 hover:underline disabled:opacity-50"
              >
                Refresh folder list
              </button>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                New folder name
              </label>
              <input
                type="text"
                value={gologinFolderInput}
                onChange={(e) => setGologinFolderInput(e.target.value)}
                className="form-input w-full text-xs"
                placeholder="e.g. VPS-3 Onboarding"
                maxLength={80}
                disabled={gologinFolderSaving}
              />
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              onClick={saveGologinFolder}
              disabled={gologinFolderSaving || !gologinFolderInput.trim()}
              className="btn-primary !px-3 disabled:opacity-40"
            >
              {gologinFolderSaving ? 'Saving…' : 'Save'}
            </button>
            {agent.gologinFolder?.name && (
              <button
                type="button"
                onClick={disableGologinFolder}
                disabled={gologinFolderSaving}
                className="btn-secondary !px-3 text-xs disabled:opacity-40"
              >
                Disable auto-import
              </button>
            )}
          </div>
        </div>
        {!compact && (
          <p className="text-[11px] text-gray-500">
            When set, every account attached via group attach or the auto-attach hook is
            also imported to this GoLogin folder — no more &quot;Select folder&quot; step. Leave
            blank to use the manual import workflow.
          </p>
        )}
      </div>
    </>
  );
}

function DesktopRemoteControl({ agent, onAgentUpdated }) {
  const [busy, setBusy] = useState('');
  const pairings = agent?.desktopPairings || [];
  if (!pairings.length) return null;

  async function send(pairingId, action) {
    const key = `${pairingId}:${action}`;
    setBusy(key);
    try {
      await api.post(`/admin/va/ai-agents/${agent._id}/desktop-control`, {
        action,
        pairingId,
        actor: 'operator'
      });
      toast.success(`${action[0].toUpperCase()}${action.slice(1)} queued. The Wingr Bot window on that VPS picks it up within a few seconds.`);
      if (onAgentUpdated) await onAgentUpdated();
    } catch (err) {
      toast.error(err.response?.data?.error || `Could not ${action}`);
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-900/40 p-4 space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">
        Wingr Bot on the VPS
      </h4>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Start, stop, or update the agent. The window opens again after the VPS reboots.
      </p>
      {pairings.map((pairing) => {
        const id = String(pairing._id);
        const control = pairing.control;
        const pending = control && (control.status === 'pending' || control.status === 'running');
        return (
          <div key={id} className="space-y-2">
            <p className="text-xs text-gray-600 dark:text-gray-300">
              <span className="font-semibold text-gray-900 dark:text-white">{pairing.label || 'Desktop'}</span>
              {pairing.host ? ` · ${pairing.host}` : ''}
              {` · window ${pairing.supervisorOnline ? 'online' : 'offline'}`}
              {` · worker ${pairing.workerRunning ? 'running' : 'stopped'}`}
            </p>
            {pending && (
              <p className="text-xs text-sky-600 dark:text-sky-300">
                {control.action} {control.status}
                {control.message ? ` — ${control.message}` : ''}
              </p>
            )}
            {control && control.status === 'failed' && control.message && (
              <p className="text-xs text-rose-500">{control.message}</p>
            )}
            <div className="flex flex-wrap gap-2">
              {['start', 'stop', 'update'].map((action) => (
                <button
                  key={action}
                  type="button"
                  disabled={Boolean(busy) || Boolean(pending)}
                  onClick={() => send(id, action)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-blue-500 to-sky-500 shadow-sm disabled:opacity-40"
                >
                  {busy === `${id}:${action}` ? 'Sending…' : action[0].toUpperCase() + action.slice(1)}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Desktop pairing + GoLogin token/folder setup for a VA agent.
 * Self-contained GoLogin state; call onAgentUpdated after saves so parents can refresh.
 */
export function AgentDesktopSetup({
  agent,
  onAgentUpdated,
  onCopy,
  compact = false,
  initialPairingId,
  initialToken,
  showRuntimeMeta = true
}) {
  if (!agent) {
    return (
      <div className="text-sm text-gray-500 p-4">Select an agent to configure desktop &amp; GoLogin.</div>
    );
  }

  const wrapClass = compact ? 'space-y-4' : 'space-y-5';
  const desktopCard = compact
    ? 'rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/60 p-4'
    : '';

  return (
    <div className={wrapClass}>
      {compact && (
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-indigo-50 dark:bg-indigo-950/40 p-2">
            <Cog6ToothIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-300" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{agent.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Pair desktops, download the worker, and set GoLogin credentials.
            </p>
          </div>
        </div>
      )}

      <div className={desktopCard || undefined}>
        {compact && (
          <h4 className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400 mb-3">
            Desktop pairing
          </h4>
        )}
        <DesktopSetupPanel
          agent={agent}
          initialPairingId={initialPairingId}
          initialToken={initialToken}
          onCopy={onCopy}
          compact={compact}
        />
      </div>

      <DesktopRemoteControl agent={agent} onAgentUpdated={onAgentUpdated} />

      <GoLoginSetupSection
        agent={agent}
        onAgentUpdated={onAgentUpdated}
        compact={compact}
      />

      {showRuntimeMeta && (
        <div className={`text-xs text-gray-500 dark:text-gray-400 space-y-1 ${compact ? 'pt-1' : 'border-t border-gray-200 dark:border-gray-700 pt-3'}`}>
          {agent.runtime?.lastSeenAt && (
            <p>Last seen: {new Date(agent.runtime.lastSeenAt).toLocaleString()}</p>
          )}
          {agent.runtime?.host && (
            <p>Host: <code>{agent.runtime.host}</code> ({agent.runtime.platform || '?'})</p>
          )}
          {agent.runtime?.version && (
            <p>Worker version: {agent.runtime.version}</p>
          )}
        </div>
      )}
    </div>
  );
}

/** Slide-over drawer for VPS Overview / Automations. */
export function AgentDesktopSetupDrawer({ agent, open, onClose, onAgentUpdated }) {
  if (!open || !agent) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close configure drawer"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg h-full bg-white dark:bg-gray-900 shadow-2xl border-l border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/30 flex-shrink-0">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Configure agent</h3>
            <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[16rem]">{agent.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <AgentDesktopSetup
            agent={agent}
            compact
            onAgentUpdated={onAgentUpdated}
          />
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 flex-shrink-0">
          <button type="button" onClick={onClose} className="btn-primary text-sm">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default AgentDesktopSetup;
